/** @jsxImportSource @emotion/react */
import { useTheme } from '@mui/material/styles'
import { AnimatePresence, motion } from 'framer-motion'
import { Resizable } from 're-resizable'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  sideDrawer,
  sideDrawerButton,
  sideDrawerContent,
  toggleButtonParent,
} from './SideDrawer.styles'
import { ToggleArrowButton } from '@app/components/Atoms/VerticalThumbTab'
import { GenericMapContextInterface } from '@app/contexts/ModuleMapContexts/GenericMapContext/GenericMapContext.types'

const noCursorClassName = 'pointer-events-none'

export interface SideDrawerProps {
  isOpen: boolean
  mapContext: GenericMapContextInterface
  side?: 'left' | 'right' | 'top' | 'bottom'
  handleToggle?: React.Dispatch<React.SetStateAction<boolean>>
  children: JSX.Element | React.ReactNode
  initialWidth?: number
  tabIcons?: string[]
  tabs?: string[]
  tab?: string
  color?: string
  setTab?: (tabIcon: string) => void
  handleWidthChange?: (width: number) => void
  hasToggleArrow?: boolean
  overrideWidth?: number
}

export const SideDrawer = ({
  isOpen,
  mapContext,
  side = 'left',
  handleToggle,
  initialWidth = 500,
  children,
  tabs = [],
  tabIcons = [],
  tab,
  setTab,
  color,
  handleWidthChange,
  hasToggleArrow = true,
  overrideWidth,
}: SideDrawerProps) => {
  const theme = useTheme()
  const [width, setWidth] = useState(initialWidth)
  const preOverrideWidthRef = useRef<number | null>(null)
  const { setMapPaddingIsChanging } = mapContext

  // Because both tab & tabIcons are in the same order, we can use the index of a tab to get its icon
  const getTabIcon = useCallback(
    (tab: string) => {
      if (!tabIcons || !tabs) return
      return tabIcons[tabs.indexOf(tab)]
    },
    [tabs, tabIcons],
  )

  // Handles the different states of the buttons, i.e. if it's active, open, etc
  const onButtonClick = useCallback(
    (clicked_tab: string) => {
      if (handleToggle && (tab === clicked_tab || !isOpen)) {
        handleToggle(!isOpen)
      }
      setTab?.(clicked_tab)
    },
    [tab, isOpen, setTab, handleToggle],
  )

  useEffect(() => {
    if (isOpen && tabs && tabs.length > 0 && tab === undefined) {
      setTab?.(tabs[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tabs])

  // Handles when isOpen is changed
  useEffect(() => {
    handleWidthChange?.(isOpen ? width : 20)
    setMapPaddingIsChanging({
      left: false,
      right: false,
      top: false,
      bottom: false,
    })
    // Re-measure the map once the 0.4s panel width transition has settled,
    // otherwise Mapbox would measure the container mid-transition.
    const resizeTimeout = setTimeout(() => {
      mapContext?.map?.resize()
    }, 450)
    return () => {
      clearTimeout(resizeTimeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, isOpen, mapContext?.map])

  useEffect(() => {
    if (tabs && tab !== undefined) {
      if (!tabs.includes(tab)) {
        if (tabs.length > 0) {
          setTab?.(tabs[0])
        } else {
          if (handleToggle) handleToggle(false)
        }
      } else {
        setTab?.(tab)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, tabs])

  // EXPERIMENTAL FEATURE:
  // This increases the width of the panel when the Risk Table is shown.
  // Design is in flux so this needs revisiting once finalised.
  useEffect(() => {
    if (overrideWidth !== undefined) {
      preOverrideWidthRef.current = width
      setWidth(overrideWidth)
    } else if (preOverrideWidthRef.current !== null) {
      setWidth(preOverrideWidthRef.current)
      preOverrideWidthRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideWidth])

  // if escape key is pressed, close the layers menu
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isOpen) {
          if (handleToggle) handleToggle(false)
          event.stopPropagation()
          event.stopImmediatePropagation()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const arrowDirection = (
    isOpen
      ? {
          left: 'left',
          right: 'right',
          top: 'up',
          bottom: 'down',
        }
      : {
          left: 'right',
          right: 'left',
          top: 'down',
          bottom: 'up',
        }
  )[side] as 'left' | 'right' | 'up' | 'down'

  return (
    <Resizable
      css={sideDrawer({ theme, isOpen, color })}
      minHeight="100%"
      maxWidth={isOpen ? '70vw' : '20px'}
      onResizeStart={() => {
        setMapPaddingIsChanging({
          left: side == 'left',
          right: side == 'right',
          top: side == 'top',
          bottom: side == 'bottom',
        })
      }}
      onResizeStop={(_e, _direction, ref, _d) => {
        setWidth(ref.offsetWidth)
      }}
      size={{
        width,
        height: '100%',
      }}
      enable={{
        top: side == 'bottom' && isOpen,
        right: side == 'left' && isOpen,
        bottom: side == 'top' && isOpen,
        left: side == 'right' && isOpen,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      handleClasses={{
        right: isOpen ? 'right-handle' : noCursorClassName,
        left: isOpen ? 'right-handle' : noCursorClassName,
      }}
    >
      {tabs && tabs.length > 0
        ? tabs.map((this_tab, index) => (
            <div css={toggleButtonParent({ theme, side })} key={this_tab}>
              <ToggleArrowButton
                css={sideDrawerButton({ theme, index, side })}
                arrowPosition="left"
                color={color}
                side={side}
                onClick={() => onButtonClick(this_tab)}
                initialIcon={this_tab ? getTabIcon(this_tab) : null}
                expanded={isOpen && this_tab === tab}
                label={this_tab}
              />
            </div>
          ))
        : hasToggleArrow && (
            <div css={toggleButtonParent({ theme, side })}>
              <ToggleArrowButton
                css={sideDrawerButton({ theme, index: 0, side })}
                side={side}
                color={color}
                arrowPosition={arrowDirection}
                onClick={() => {
                  if (handleToggle) handleToggle(!isOpen)
                }}
              />
            </div>
          )}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            css={sideDrawerContent({ theme })}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </Resizable>
  )
}
